// REPRESENTATIVE CONTENT · 대표 콘텐츠 — 썸네일 갤러리(조회수순 자동), 한 줄 가로 스크롤.
//
// Overview와 소스 공유(BrandedContentItem, bi_base_data sponsored). 수동 핀은 Phase 2.
// 카드: 썸네일(플랫폼 배지 + 조회수 오버레이 + 재생 아이콘) + 브랜드·상품 + 제목(ellipsis).
// 빈 배열이면 섹션 생략(graceful). 썸네일 로드 실패 시 다크 배경 폴백.
//
// 배치는 Overview ③(BrandedContentCarousel)과 **같은 캐러셀 패턴**이다 — 좌우 스크롤 +
// 엣지 페이드 + 이전/다음 버튼. 줄바꿈 그리드로 두면 "조회수 상위 12개"라는 한 줄의 서열이
// 여러 줄로 흩어져 읽히지 않는다. 페이드는 bg-base 파생(다크 안전).
import { useCallback, useEffect, useRef, useState } from 'react'
import { ChevronLeft, ChevronRight, Play } from 'lucide-react'
import type { BrandedContentItem, DataMeta , Platform, ChannelSubs } from '../../creatorsApi'
import { InfoTip } from '../InfoTip'
import { PLATFORM_LABEL, buildBasisNote, toWatchUrl } from '../overview/format'
import { SectionHeader } from './SectionHeader'
import { AiJudgedNotice } from '../AiJudgedNotice'
import { channelEmptyText } from '../platformDefault'
import { PlatformTabs } from '../PlatformTabs'
import { formatInt } from './format'

// 기존 썸네일 높이(150px) 그대로. 카드 폭도 이 값에서 판다(Overview ③은 같은 규칙의 300px 기준).
const CARD_H = 150

export function RepresentativeContent({
  items,
  meta,
  platform,
  onPlatformChange,
  busy,
  channels,
}: {
  items: BrandedContentItem[]
  meta?: DataMeta
  /** 탭을 붙이려면 둘 다 준다. 없으면 탭 없이 렌더(기존 호출부 호환). */
  platform?: Platform
  onPlatformChange?: (platform: Platform) => void
  /** 새 플랫폼 응답 대기 중 — 탭 옆에 표시한다(아래 값은 아직 이전 플랫폼 것이다). */
  busy?: boolean
  /** 사유 판정 입력 — 계정 없음 / 수집 대상 아님 / 브랜디드 없음을 가른다. */
  channels?: ChannelSubs[]
}) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [fadeL, setFadeL] = useState(0)
  const [fadeR, setFadeR] = useState(1)

  const onScroll = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    const max = el.scrollWidth - el.clientWidth
    setFadeL(Math.max(0, Math.min(1, el.scrollLeft / 40)))
    setFadeR(max <= 1 ? 0 : Math.max(0, Math.min(1, (max - el.scrollLeft) / 40)))
  }, [])

  // 플랫폼 탭을 바꾸면 카드 수가 달라져 스크롤 여지도 달라진다 — 페이드/버튼을 다시 잰다.
  useEffect(() => {
    onScroll()
  }, [onScroll, items])

  function scrollByDir(dir: 1 | -1) {
    const el = scrollRef.current
    if (!el) return
    el.scrollBy({ left: dir * Math.round(el.clientWidth * 0.7), behavior: 'smooth' })
  }

  // 탭이 붙은 블록은 비어도 숨기지 않는다 — 다른 플랫폼으로 갈 길이 사라진다.
  const empty = items.length === 0

  return (
    <section className="mt-[34px]">
      {/* Overview ③과 같은 소스라 고지 문구도 같은 빌더에서 나온다(두 블록이 갈라지지 않게) */}
      <SectionHeader
        label="REPRESENTATIVE CONTENT · 대표 콘텐츠"
        info={
          <>
            <InfoTip
              label="대표 콘텐츠 선정 기준"
              // Overview 캐러셀과 **같은 소스·같은 기준**이라 문구도 같아야 한다 — 다르면 두 블록이
              // 서로 다른 선별 규칙을 주장하게 된다.
              text="협찬 콘텐츠를 확정 조회수 순으로 정렬해 상위 12개를 보여줍니다."
            />
            {platform && onPlatformChange && (
              <PlatformTabs value={platform} onChange={onPlatformChange} label="대표 콘텐츠 플랫폼" busy={busy} />
            )}
          </>
        }
        // platform 을 넘기지 않는다 — AI 고지는 아래 전용 줄이 말하므로 여기 붙으면 중복이다.
        note={buildBasisNote('협찬 콘텐츠 · 확정 조회수 상위', meta)}
      />
      <AiJudgedNotice className="mb-4" />
      {empty && (
        <div className="border border-line-hairline bg-surface-base p-8 text-center font-text text-[13px] text-fg-3">
          {channelEmptyText(platform ?? 'youtube', channels)}
        </div>
      )}
      <div className="relative" hidden={empty}>
        <div ref={scrollRef} onScroll={onScroll} className="flex gap-4 overflow-x-auto pb-4">
          {items.map((item) => {
            // 폭 규칙도 Overview ③과 같다 — 종횡비 0.72~1.5 클램프 × 카드 높이. 카드 높이만
            // 이 블록의 기존 150px 을 쓴다(갤러리 카드 크기는 이번 범위 밖). 썸네일은 cover 크롭.
            const ar = Math.min(1.5, Math.max(0.72, item.aspectRatio ?? 1))
            const w = Math.round(ar * CARD_H)
            const inner = (
              <>
                <div className="relative overflow-hidden bg-surface-ink" style={{ height: CARD_H }}>
                  {item.thumbnailUrl && (
                    <img
                      src={item.thumbnailUrl}
                      alt=""
                      aria-hidden
                      className="absolute inset-0 h-full w-full object-cover"
                      onError={(e) => {
                        ;(e.currentTarget as HTMLImageElement).style.display = 'none'
                      }}
                    />
                  )}
                  <div
                    className="pointer-events-none absolute inset-0"
                    style={{
                      background:
                        'linear-gradient(180deg, rgba(0,0,0,0.30) 0%, rgba(0,0,0,0) 34%, rgba(0,0,0,0) 58%, rgba(0,0,0,0.46) 100%)',
                    }}
                  />
                  <span className="absolute left-1.5 top-1.5 bg-black/50 px-2 py-0.5 font-text text-[11px] tracking-[0.03em] text-white">
                    {PLATFORM_LABEL[item.platform]}
                  </span>
                  {/* 0 은 "—" 로 그린다 — Overview ③ 캐러셀과 같은 규칙이다. Instagram 이미지·
                      캐러셀 게시물은 플랫폼이 조회수를 주지 않아(영상에만 준다) 0 으로 내려온다.
                      "상위 12개"에 이 게시물이 섞이는 건 조회수 있는 것이 12개가 안 될 때인데,
                      그대로 "0"을 그리면 아무도 보지 않은 협찬으로 읽힌다. */}
                  <span className="absolute bottom-1.5 right-1.5 bg-black/55 px-2 py-px font-text text-[11px] tabular-nums text-white">
                    {item.views ? formatInt(item.views) : '—'}
                  </span>
                  <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                    <span className="flex h-[26px] w-[26px] items-center justify-center rounded-full bg-white/90">
                      <Play className="h-2.5 w-2.5 fill-current text-fg-1" aria-hidden />
                    </span>
                  </div>
                </div>
                {/* 타이포는 Overview ③ 캐러셀의 소제목과 동일하게 둔다(font-text 12px semibold).
                    font-display 14px 는 카드 제목급이라, 브랜드·상품이 영상 제목보다 커 보였다. */}
                <div className="mt-2 truncate font-text text-[12px] font-semibold leading-[1.35] text-ink-800">
                  {[item.brand, item.product].filter(Boolean).join(' · ')}
                </div>
                <div className="mt-0.5 truncate font-text text-[11px] text-fg-2">{item.title}</div>
              </>
            )
            // url 있으면 카드 전체를 새 탭 링크로(시각은 그대로, 클릭만 부여). 없으면 기존 비클릭.
            return item.url ? (
              <a
                key={item.id}
                href={toWatchUrl(item.url)}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-none cursor-pointer"
                style={{ width: w }}
              >
                {inner}
              </a>
            ) : (
              <article key={item.id} className="flex-none" style={{ width: w }}>
                {inner}
              </article>
            )
          })}
        </div>

        {/* 엣지 페이드 (bg-base 파생 — 다크 안전) */}
        <div
          className="pointer-events-none absolute inset-y-0 left-0 w-20"
          style={{
            background: 'linear-gradient(90deg, var(--bg-base), transparent)',
            opacity: fadeL,
          }}
        />
        <div
          className="pointer-events-none absolute inset-y-0 right-0 w-20"
          style={{
            background: 'linear-gradient(270deg, var(--bg-base), transparent)',
            opacity: fadeR,
          }}
        />

        {/* 버튼은 썸네일 세로 중앙(카드 높이 절반) — 아래 브랜드·제목 줄을 덮지 않는다. */}
        <button
          type="button"
          onClick={() => scrollByDir(-1)}
          aria-label="이전"
          className="absolute left-3 top-[75px] z-[2] flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-line-hairline bg-surface-base shadow-lf-2"
          style={{ opacity: fadeL, pointerEvents: fadeL > 0 ? 'auto' : 'none' }}
        >
          <ChevronLeft className="h-5 w-5 text-fg-1" strokeWidth={2.4} />
        </button>
        <button
          type="button"
          onClick={() => scrollByDir(1)}
          aria-label="다음"
          className="absolute right-3 top-[75px] z-[2] flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-line-hairline bg-surface-base shadow-lf-2"
          style={{ opacity: fadeR, pointerEvents: fadeR > 0 ? 'auto' : 'none' }}
        >
          <ChevronRight className="h-5 w-5 text-fg-1" strokeWidth={2.4} />
        </button>
      </div>
    </section>
  )
}
